'use client'

import { useEffect, useRef, useState } from 'react'
import { Box, Focus, Orbit, RotateCcw } from 'lucide-react'
import type { AtlasAnatomyPart } from '@/data/grass-atlas-morphology'
import type { ProceduralGrassModelSpec } from './model-contract'
import styles from '../grass-atlas.module.css'
import type { GrassSimulationVisualState } from '../grass-simulation'

type SceneController = import('./procedural-grass-three').ProceduralGrassScene

const HOTSPOT_PARTS: AtlasAnatomyPart[] = [
  'seedhead',
  'blade-tip',
  'blade',
  'collar',
  'node',
  'runner',
  'roots',
]

const PART_LABELS: Record<AtlasAnatomyPart, string> = {
  'blade-tip': 'blade tip',
  blade: 'leaf blade',
  collar: 'collar, ligule, and auricles',
  node: 'node and sheath',
  runner: 'growth habit and runners',
  roots: 'roots and below-soil growth',
  seedhead: 'seedhead',
}

interface ProceduralGrassViewerProps {
  spec: ProceduralGrassModelSpec
  activePart: AtlasAnatomyPart
  dissected: boolean
  onPartChange: (part: AtlasAnatomyPart) => void
  onUnavailable: (error: unknown, stage: string) => void
  onReady?: (durationMs: number) => void
  simulation: GrassSimulationVisualState
  simulationActive: boolean
  showNumbers: boolean
}

function modelBadge(spec: ProceduralGrassModelSpec) {
  if (spec.scope === 'species-group') {
    return {
      label: `${spec.displayName} 3D group plate`,
      note: 'Shared traits from several related grasses',
    }
  }
  if (spec.scope === 'mixture') {
    return {
      label: 'Example 3D mixed stand',
      note: 'Not one plant or a fixed seed formula',
    }
  }
  return {
    label: 'Source-linked 3D teaching model',
    note: 'Key features enlarged for learning',
  }
}

function growthLabel(spec: ProceduralGrassModelSpec) {
  if (spec.scope === 'mixture') return 'Shoots'
  if (['stolon', 'both'].includes(spec.growth.form)) return 'Stolon'
  if (['rhizome', 'bunch-rhizome'].includes(spec.growth.form)) return 'Rhizome'
  return 'Crown'
}

export function ProceduralGrassViewer({
  spec,
  activePart,
  dissected,
  onPartChange,
  onUnavailable,
  onReady,
  simulation,
  simulationActive,
  showNumbers,
}: ProceduralGrassViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const controllerRef = useRef<SceneController | null>(null)
  const simulationRef = useRef(simulation)
  simulationRef.current = simulation
  const previousPartRef = useRef(activePart)
  const hotspotRefs = useRef<Partial<Record<AtlasAnatomyPart, HTMLButtonElement | null>>>({})
  const [ready, setReady] = useState(false)
  const badge = modelBadge(spec)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    let cancelled = false
    let loadStage = 'model-import'
    const startedAt = performance.now()
    let intersectionObserver: IntersectionObserver | null = null
    let isIntersecting = true
    setReady(false)

    const syncVisibility = () => {
      controllerRef.current?.setPaused(
        document.visibilityState !== 'visible' || !isIntersecting,
      )
    }

    const handleContextLost = (event: Event) => {
      event.preventDefault()
      onUnavailable(new Error('The browser lost the WebGL 2 graphics context'), 'context-lost')
    }

    canvas.addEventListener('webglcontextlost', handleContextLost)

    void import('./procedural-grass-three')
      .then(({ ProceduralGrassScene }) => {
        if (cancelled) return
        loadStage = 'renderer-init'
        const controller = new ProceduralGrassScene(canvas, {
          spec,
          reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        })
        for (const part of HOTSPOT_PARTS) {
          const element = hotspotRefs.current[part]
          if (element) controller.attachHotspot(part, element)
        }
        controller.setDissected(dissected)
        controller.setActivePart(activePart, false)
        controller.setSimulation(simulationRef.current, true)
        controllerRef.current = controller
        previousPartRef.current = activePart
        setReady(true)
        onReady?.(performance.now() - startedAt)

        intersectionObserver = new IntersectionObserver(([entry]) => {
          isIntersecting = Boolean(entry?.isIntersecting)
          controller.setPaused(!isIntersecting || document.visibilityState !== 'visible')
        }, { rootMargin: '160px' })
        intersectionObserver.observe(canvas)
        document.addEventListener('visibilitychange', syncVisibility)
      })
      .catch((error) => {
        if (!cancelled) onUnavailable(error, loadStage)
      })

    return () => {
      cancelled = true
      intersectionObserver?.disconnect()
      document.removeEventListener('visibilitychange', syncVisibility)
      canvas.removeEventListener('webglcontextlost', handleContextLost)
      controllerRef.current?.dispose()
      controllerRef.current = null
    }
  }, [spec, onReady, onUnavailable])

  useEffect(() => {
    const shouldFocus = ready && previousPartRef.current !== activePart
    controllerRef.current?.setActivePart(activePart, shouldFocus)
    previousPartRef.current = activePart
  }, [activePart, ready])

  useEffect(() => {
    controllerRef.current?.setDissected(dissected)
  }, [dissected])

  useEffect(() => {
    controllerRef.current?.setSimulation(simulation)
  }, [simulation])

  return (
    <div className={styles.threeStage}>
      <canvas
        ref={canvasRef}
        className={styles.threeCanvas}
        aria-label={`Interactive 3D ${spec.displayName} model. Drag to turn it and scroll or pinch to zoom${showNumbers ? ', then use the numbered markers to explore each part' : ''}.`}
      />

      <div className={styles.threeAtmosphere} aria-hidden="true" />

      <div className={styles.threeModelBadge}>
        <Box size={14} />
        <span>
          <strong>{badge.label}</strong>
          {badge.note}
        </span>
      </div>

      <div className={styles.threeInstructions} aria-hidden="true">
        <Orbit size={15} />
        {showNumbers ? 'Drag to turn · zoom · tap a number' : 'Drag to turn · zoom'}
      </div>

      <div className={styles.threeHotspots} hidden={simulationActive || !showNumbers}>
        {HOTSPOT_PARTS.map((part, index) => (
          <button
            type="button"
            key={part}
            ref={(element) => {
              hotspotRefs.current[part] = element
            }}
            className={`${styles.threeHotspot} ${activePart === part ? styles.threeHotspotActive : ''}`}
            onClick={() => onPartChange(part)}
            aria-label={`Inspect ${PART_LABELS[part]}`}
            aria-pressed={activePart === part}
          >
            <span>{index + 1}</span>
          </button>
        ))}
      </div>

      <div className={styles.threeFocusRail} aria-label="3D camera views" hidden={simulationActive}>
        <button
          type="button"
          onClick={() => {
            onPartChange('blade-tip')
            controllerRef.current?.focusPart('blade-tip')
          }}
        >
          <Focus size={14} />
          Tip
        </button>
        <button
          type="button"
          onClick={() => {
            onPartChange('collar')
            controllerRef.current?.focusPart('collar')
          }}
        >
          Collar
        </button>
        <button
          type="button"
          onClick={() => {
            onPartChange('runner')
            controllerRef.current?.focusPart('runner')
          }}
        >
          {growthLabel(spec)}
        </button>
        <button type="button" aria-label="Reset 3D camera" onClick={() => controllerRef.current?.reset()}>
          <RotateCcw size={14} />
        </button>
      </div>

      {!ready ? (
        <div className={styles.threeLoading} role="status">
          <span />
          Building botanical specimen
        </div>
      ) : null}

      <div className={styles.specimenCaption}>
        <span>{spec.displayName} · {spec.scientificName}</span>
        <span>{spec.scope === 'species' ? 'Rotate or choose a focus view' : 'Scoped teaching plate · rotate or choose a focus view'}</span>
      </div>
    </div>
  )
}
